import React, { useState, useEffect } from 'react';
import {
    Box,
    Modal,
    TextField,
    IconButton,
    FormControl,
    Autocomplete,
    Typography,
    Divider,
    ListSubheader,
    Grid2,
    Paper,
    Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, useFieldArray } from 'react-hook-form';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DeepLinkSwitch } from '../service/DeepLinkSwitch';
import { toast } from 'react-toastify';
import CustomTextField from '../settings/commonTextinput';
import CommonButton from '../settings/commonButton';
import SecondaryHeading from '../settings/commonSecondaryHeading';
import DeleteIcon from '../../assets/DeleteIcon.png';
import CustomDatePicker from '../settings/commonDatePicker';
import CustomTimePicker from '../settings/commonTimePicker';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import FSelect from '../commonComponents/F_Select';
import { t } from 'i18next';
import { CreateCampaignOffer, UpdateCampaignOffer } from '../../utils/Api/SpecialOffers';
import { GetServiceGroup } from '../../utils/Api/Service';
import { transformServiceList } from '../calanderComponents/booking/utils/functions';
import { useSelector } from 'react-redux';
import { getOfferScheduleValidationFields } from './offerScheduleValidation';

function AddCampaignOfferModal(props) {
    const { user, open, handleClose, selectedCampaignOffer, type } = props;
    const canEditOffers = user?.role === 'ADMIN' || Boolean(user?.settings?.crud_special_offers);

    const profileEmployees = useSelector(
        (state) => state.settings?.data?.profile?.employees || state.settings?.data?.employees || [],
    );

    const [serviceList, setServiceList] = useState([]);
    const [searchText, setSearchText] = useState('');

    const initialValues = {
        start_date: null,
        start_time: null,
        ending_date: null,
        ending_time: null,
        endOf_Offer: '',
        customStartDate: '',
        customEndDate: '',
        customStartTime: '',
        customEndTime: '',
        campaignTitle: '',
        isActive: false,
    };

    const {
        control,
        register,
        setValue,
        getValues,
        watch,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm({
        defaultValues: {
            services: [],
        },
        shouldUnregister: false,
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'services',
    });

    const watchedServices = watch('services');

    const validateEmployeeSelections = () => {
        const services = getValues('services') || [];
        let hasEmployeeError = false;

        services.forEach((field, index) => {
            if (!field.employeeId || field.employeeId.length === 0) {
                setError(`services.${index}.employeeId`, {
                    type: 'manual',
                    message: t('SpOffers.YupErrSelectedEmployeesRequired'),
                });
                hasEmployeeError = true;
            } else {
                clearErrors(`services.${index}.employeeId`);
            }
        });

        return !hasEmployeeError;
    };

    useEffect(() => {
        if (type == 'edit') {
            const startDate = new Date(selectedCampaignOffer?.start_datetime);
            const endDate = new Date(selectedCampaignOffer?.end_datetime);

            formik.setValues({
                ...formik.values,
                start_date: moment(startDate),
                start_time: moment(startDate),
                ending_date: moment(endDate),
                ending_time: moment(endDate),
                campaignTitle: selectedCampaignOffer?.title,
                marketplace_hidden: selectedCampaignOffer.marketplace_hidden || false,
            });

            selectedCampaignOffer?.services.forEach((item) => {
                const service = item.service;
                const activeEmployeeIds = new Set((profileEmployees || []).map((emp) => emp.id));
                const selectedEmployeeIds = (item.employees || [])
                    .map((emp) => emp.id)
                    .filter((id) => activeEmployeeIds.has(id));
                append({
                    id1: item.id,
                    serviceId: service.id,
                    newPrice: service.special_price,
                    percentage: service.special_percentage,
                    oldPrice: service.price,
                    dropdownValue: '',
                    employeeId: selectedEmployeeIds,
                    serviceName: service.name,
                    employees: profileEmployees || [],
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCampaignOffer]);

    useEffect(() => {
        fetchServiceApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function fetchServiceApi() {
        // setLoading(true);
        try {
            const response = await GetServiceGroup();

            if (response) {
                setServiceList(transformServiceList(response.data.data, fields, t));
            } else {
                setServiceList([]);
            }
        } catch (error) {
            console.error('Error fetching service data:', error);
            setServiceList([]);
        }
    }

    useEffect(() => {
        fetchServiceApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields.length]);

    const handleServiceSelect = (service) => {
        const updatedServices = serviceList.map((group) => ({
            ...group,
            data: group.data.filter((serviceItem) => serviceItem.id !== service.id),
        }));

        setServiceList(updatedServices);

        append({
            serviceId: service.id,
            newPrice: '',
            oldPrice: service.price,
            dropdownValue: '',
            employeeId: service.employees.map((employee) => employee.id),
            serviceName: service.name,
            employees: profileEmployees || [],
            percentage: '',
        });
    };

    const resetFields = (command) => {
        handleClose(command);
    };

    const handleCloseModal = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
    };

    const validationSchema = Yup.object({
        ...getOfferScheduleValidationFields(),
        campaignTitle: Yup.string().required(t('SpOffers.YupErrCampaignTitleRequired')),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            const updatedFields = getValues('services');

            let startDate = null;
            let startTime = null;
            let endDate = null;
            let endTime = null;

            if (!values.isActive) {
                startDate = moment(values.start_date).format('YYYY-MM-DD');
                startTime = moment(values.start_time).format('HH:mm:ss');
                endDate = moment(values.ending_date).format('YYYY-MM-DD');
                endTime = moment(values.ending_time).format('HH:mm:ss');
            } else {
                startDate = moment().format('YYYY-MM-DD');
                startTime = moment().format('HH:mm:ss');
                endDate = moment(values.ending_date).format('YYYY-MM-DD');
                endTime = moment(values.ending_time).format('HH:mm:ss');
            }

            if (updatedFields.length == 0) {
                toast.error(t('SpOffers.ToastErrOneServ'));
                return;
            }

            if (!validateEmployeeSelections()) {
                return;
            }

            const specialOffers = updatedFields
                .map((field) => {
                    const serviceId = field.serviceId;
                    const specialPrice = Number(field.newPrice);

                    let formattedPercentage = Number(field.percentage);
                    if (isNaN(formattedPercentage)) {
                        formattedPercentage = parseFloat(field.percentage);
                    }

                    formattedPercentage =
                        formattedPercentage % 1 === 0
                            ? formattedPercentage
                            : formattedPercentage.toFixed(2).replace('.00', '');

                    const employeeIds = field.employeeId;

                    const isPriceMissing =
                        field.newPrice === '' ||
                        field.newPrice === null ||
                        field.newPrice === undefined ||
                        Number.isNaN(specialPrice);
                    const isPercentageMissing =
                        field.percentage === '' ||
                        field.percentage === null ||
                        field.percentage === undefined ||
                        Number.isNaN(Number(formattedPercentage));

                    if (isPriceMissing || isPercentageMissing) {
                        toast.error(t('SpOffers.ToastErrPrPer'));
                        return null;
                    }

                    return type === 'edit' && selectedCampaignOffer?.services?.length > 0
                        ? {
                              service_id: serviceId,
                              special_price: specialPrice,
                              special_percentage: formattedPercentage,
                              employee_ids: employeeIds,
                              id: field.id1,
                          }
                        : {
                              service_id: serviceId,
                              special_price: specialPrice,
                              special_percentage: formattedPercentage,
                              employee_ids: employeeIds,
                          };
                })
                .filter((offer) => offer !== null);

            if (specialOffers.length === 0) {
                return;
            }

            try {
                const data = {
                    campaign_name: values.campaignTitle,
                    start_datetime: `${startDate} ${startTime}`,
                    end_datetime: `${endDate} ${endTime}`,
                    special_offers: specialOffers,
                    marketplace_hidden: values.marketplace_hidden,
                };

                if (type === 'create') {
                    const response = await CreateCampaignOffer({ data });

                    if (response) {
                        toast.success(t('SpOffers.ToastSCmpOffCr'));
                        resetFields('callApi');
                    } else {
                        toast.error(t('SpOffers.ToastErrCmpCr'));
                    }
                } else if (type === 'edit') {
                    const response = await UpdateCampaignOffer({
                        id: selectedCampaignOffer?.groupId,
                        data,
                    });

                    if (response) {
                        toast.success(t('SpOffers.ToastSUpCmpOff'));
                        resetFields('callApi');
                    } else {
                        toast.error(t('SpOffers.ToastErrCmpUp'));
                    }
                } else {
                    toast.error(t('SpOffers.ToastErrCmpOff'));
                }
            } catch (error) {
                console.error(`Error while ${type} capaign offer:`, error);
            }
        },
    });

    const handleCampaignSubmit = async () => {
        validateEmployeeSelections();

        const formikErrors = await formik.validateForm();
        formik.setTouched(
            {
                start_date: true,
                start_time: true,
                ending_date: true,
                ending_time: true,
                campaignTitle: true,
                isActive: true,
            },
            false,
        );

        if (Object.keys(formikErrors).length > 0) {
            return;
        }

        formik.handleSubmit();
    };

    const handleCheckboxChange = (e, index) => {
        const selectedValues = e.target.value; // get the selected values from the event
        const currentEmployeeIds = getValues(`services.${index}.employeeId`) || [];

        let updatedEmployeeIds;

        // If the selected value is 0 or contains 0, handle the select-all behavior
        if (selectedValues.includes(0) && currentEmployeeIds.length == (profileEmployees || []).length) {
            updatedEmployeeIds = []; // Deselect all if all are selected
        } else if (selectedValues.includes(0)) {
            updatedEmployeeIds = (profileEmployees || []).map((empObj) => empObj?.id); // Select all employees
        } else if (currentEmployeeIds.length == (profileEmployees || []).length) {
            updatedEmployeeIds = []; // Deselect all
        } else {
            updatedEmployeeIds = selectedValues; // Update employee IDs with selected ones
        }

        setValue(`services.${index}.employeeId`, updatedEmployeeIds);

        if (updatedEmployeeIds.length > 0) {
            clearErrors(`services.${index}.employeeId`);
        }
    };

    const allServices = serviceList.flatMap((groupData) =>
        groupData.data.map((service) => ({
            ...service,
            group: groupData.title,
        })),
    );

    const handleSearchInputChange = (event, value) => {
        setSearchText(value);
    };

    const filteredOptions = allServices.filter((service) =>
        service.name.toLowerCase().includes(searchText.toLowerCase()),
    );

    return (
        <Modal
            open={open}
            disableAutoFocus
            onClose={handleCloseModal}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
            <Paper
                sx={{
                    width: '80%',
                    maxHeight: '80%',
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    borderRadius: 8,
                    py: 4,
                    px: 2,
                }}
            >
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={() => resetFields()}
                        aria-label="close"
                        sx={{
                            position: 'absolute',
                            top: '10px',
                            right: '30px',
                            zIndex: 10,
                            color: 'black',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f1f1f', textAlign: 'center', mb: 4 }}>
                    {type == 'edit' ? t('SpOffers.UpCmp') : t('SpOffers.CrCmp')}
                </Typography>

                <form
                    onReset={() => formik.resetForm()}
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleCampaignSubmit();
                    }}
                >
                    <Grid2 container spacing={3}>
                        <Grid2 size={{ xs: 12, md: 3 }}>
                            {!formik.values.isActive && (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', md: 'row' },
                                            width: '100%',
                                            justifyContent: 'space-between',
                                            alignItems: formik.values.start_date === 'custom' ? null : 'center',
                                            mt: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 700,
                                                color: '#1f1f1f',
                                                width: { xs: '100%', md: '50%' },
                                                textAlign: { xs: 'left', md: 'right' },
                                                pr: 3,
                                            }}
                                        >
                                            {t('SpOffers.SetStDate')}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                width: { xs: '100%', md: '50%' },
                                            }}
                                        >
                                            <CustomDatePicker
                                                disabled={!canEditOffers}
                                                value={formik.values.start_date}
                                                onChange={(e) => {
                                                    formik.setFieldValue('start_date', e);
                                                }}
                                                sx={{ width: '100%' }}
                                                onBlur={formik.handleBlur}
                                                format="DD/MM-YYYY"
                                                size="small"
                                                borderColor="#D9D9D9"
                                                padding={1}
                                                borderThickness="2px"
                                                inputColor="#A0A0A0"
                                                iconVisibility={false}
                                            />
                                            {formik.errors.start_date &&
                                                (formik.touched.start_date || formik.submitCount > 0) && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="caption" color="red">
                                                            {formik.errors.start_date}
                                                        </Typography>
                                                    </Box>
                                                )}
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', md: 'row' },
                                            width: '100%',
                                            justifyContent: 'space-between',
                                            alignItems: formik.values.start_time === 'custom' ? null : 'center',
                                            mt: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 700,
                                                color: '#1f1f1f',
                                                width: { xs: '100%', md: '50%' },
                                                textAlign: { xs: 'left', md: 'right' },
                                                pr: 3,
                                            }}
                                        >
                                            {t('SpOffers.SetStTime')}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                width: { xs: '100%', md: '50%' },
                                            }}
                                        >
                                            <CustomTimePicker
                                                disabled={!canEditOffers}
                                                value={formik.values.start_time}
                                                onChange={(newTime) => {
                                                    formik.setFieldValue('start_time', newTime);
                                                }}
                                                sx={{ width: '100%' }}
                                            />
                                            {formik.errors.start_time &&
                                                (formik.touched.start_time || formik.submitCount > 0) && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="caption" color="red">
                                                            {formik.errors.start_time}
                                                        </Typography>
                                                    </Box>
                                                )}
                                        </Box>
                                    </Box>
                                </>
                            )}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    alignItems: formik.values.ending_date === 'custom' ? null : 'center',
                                    mt: 2,
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: '#1f1f1f',
                                        width: { xs: '100%', md: '50%' },
                                        textAlign: { xs: 'left', md: 'right' },
                                        pr: 3,
                                    }}
                                >
                                    {t('SpOffers.SetEndDate')}
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: { xs: '100%', md: '50%' },
                                    }}
                                >
                                    <CustomDatePicker
                                        disabled={!canEditOffers}
                                        value={formik.values.ending_date}
                                        onChange={(e) => {
                                            formik.setFieldValue('ending_date', e);
                                        }}
                                        sx={{ width: '100%' }}
                                        onBlur={formik.handleBlur}
                                        size="small"
                                        borderColor="#D9D9D9"
                                        format="DD/MM-YYYY"
                                        padding={1}
                                        borderThickness="2px"
                                        inputColor="#A0A0A0"
                                        iconVisibility={false}
                                    />
                                    {formik.errors.ending_date &&
                                        (formik.touched.ending_date || formik.submitCount > 0) && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="caption" color="red">
                                                    {formik.errors.ending_date}
                                                </Typography>
                                            </Box>
                                        )}
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    alignItems: formik.values.ending_time === 'custom' ? null : 'center',
                                    mt: 2,
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: '#1f1f1f',
                                        width: { xs: '100%', md: '50%' },
                                        textAlign: { xs: 'left', md: 'right' },
                                        pr: 3,
                                    }}
                                >
                                    {t('SpOffers.SetEndTime')}
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: { xs: '100%', md: '50%' },
                                    }}
                                >
                                    <CustomTimePicker
                                        disabled={!canEditOffers}
                                        value={formik.values.ending_time}
                                        onChange={(newTime) => formik.setFieldValue('ending_time', newTime)}
                                        sx={{ width: '100%' }}
                                    />
                                    {formik.errors.ending_time &&
                                        (formik.touched.ending_time || formik.submitCount > 0) && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="caption" color="red">
                                                    {formik.errors.ending_time}
                                                </Typography>
                                            </Box>
                                        )}
                                </Box>
                            </Box>
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 9 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mt: 1,
                                    mb: 3,
                                }}
                            >
                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1f1f1f' }}>
                                    {t('SpOffers.Cmp')}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    height: 45,
                                    backgroundColor: '#D9D9D9',
                                    borderRadius: '13px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <CustomTextField
                                    disabled={!canEditOffers}
                                    value={formik.values.campaignTitle}
                                    onChange={(e) => formik.setFieldValue('campaignTitle', e.target.value)}
                                    placeholder={t('SpOffers.CmpTitle')}
                                    mt={0}
                                    ml={3}
                                    height={40}
                                    borderRadius={'20px'}
                                    width={'40%'}
                                />
                            </Box>

                            {formik.errors.campaignTitle &&
                                (formik.touched.campaignTitle || formik.submitCount > 0) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '90%' }}>
                                        <Typography variant="caption" color="red">
                                            {formik.errors.campaignTitle}
                                        </Typography>
                                    </Box>
                                )}

                            <Box>
                                {fields.length > 0 && (
                                    <Box
                                        sx={{
                                            display: { xs: 'none', md: 'flex' },
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 2,
                                            mt: 2,
                                            height: 0,
                                            border: '1.5px solid transparent',
                                            borderRadius: '12px',
                                            padding: '8px',
                                            backgroundColor: '#fff',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '24%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                '@media (max-width: 1440px)': { width: '30%' },
                                                '@media (max-width: 900px)': { width: '40%' },
                                            }}
                                        >
                                            <SecondaryHeading text={''} fontColor={'#A0A0A0'} />
                                        </Box>

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 400,
                                                color: '#1F1F1F',
                                                width: '10%',
                                                '@media (max-width: 1440px)': { width: '12%' },
                                                '@media (max-width: 900px)': { width: '18%' },
                                            }}
                                        >
                                            {t('SpOffers.Percentage')}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: '15%',
                                                '@media (max-width: 1440px)': { width: '15%' },
                                                '@media (max-width: 900px)': { width: '25%', marginBottom: '8px' },
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 400,
                                                    color: '#1F1F1F',
                                                    mr: 2,
                                                }}
                                            >
                                                {t('SpOffers.OldPr')}
                                            </Typography>
                                        </Box>

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 400,
                                                color: '#1F1F1F',
                                                width: '13%',
                                                '@media (max-width: 1440px)': { width: '15%' },
                                                '@media (max-width: 900px)': { width: '18%' },
                                                ml: 1,
                                            }}
                                        >
                                            {t('SpOffers.NewPr')}
                                        </Typography>

                                        <FormControl sx={{ width: '20%' }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 400,
                                                    color: '#1F1F1F',
                                                    '@media (max-width: 1440px)': { width: '110%', mr: 2 },
                                                    '@media (max-width: 900px)': { width: '120%', mr: 2 },
                                                }}
                                            >
                                                {t('SpOffers.WhOffPr')}?
                                            </Typography>
                                        </FormControl>

                                        <Box sx={{ width: '17px' }} />
                                    </Box>
                                )}

                                {fields.map((field, index) => (
                                    <Box
                                        key={field.id}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', md: 'row' },
                                            justifyContent: 'space-between',
                                            alignItems: {
                                                xs: 'stretch',
                                                md: errors?.services?.[index]?.employeeId ? 'flex-start' : 'center',
                                            },
                                            mb: 2,
                                            minHeight: { xs: '100%', md: 43 },
                                            height: 'auto',
                                            border: '1.5px solid #ddd',
                                            borderRadius: '12px',
                                            padding: '8px',
                                            backgroundColor: '#fff',
                                            mt: { xs: 2, md: 0 },
                                            overflow: 'visible',
                                        }}
                                    >
                                        <Box
                                            id={`services.${index}.serviceName`}
                                            sx={{
                                                width: { xs: '100%', md: '23%' },
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            <SecondaryHeading
                                                sx={{ textAlign: { xs: 'center', md: 'start' } }}
                                                text={field.serviceName}
                                                fontColor={'#A0A0A0'}
                                            />
                                        </Box>

                                        <Divider
                                            sx={{
                                                display: { xs: 'block', md: 'none' },
                                                width: '100%',
                                                borderWidth: 1,
                                                borderColor: '#000',
                                            }}
                                        />

                                        <React.Fragment id={`services.${index}.percentage`}>
                                            <Typography
                                                display={{ xs: 'block', md: 'none' }}
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 400,
                                                    color: '#1F1F1F',
                                                    mt: 2,
                                                    width: { xs: '100%', md: '10%' },
                                                }}
                                            >
                                                {t('SpOffers.Percentage')}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    width: { xs: '100%', md: '23%' },
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <TextField
                                                    {...register(`services.${index}.percentage`)}
                                                    name={`services.${index}.percentage`}
                                                    disabled={!canEditOffers}
                                                    size="small"
                                                    onKeyPress={(e) => {
                                                        const regex = /^[0-9]/;
                                                        if (
                                                            !regex.test(e.key) &&
                                                            e.key !== 'Backspace' &&
                                                            e.key !== 'Tab'
                                                        ) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value === '') {
                                                            setValue(`services.${index}.newPrice`, '');
                                                        } else {
                                                            const percentage = parseFloat(value);
                                                            let newPrice = field.oldPrice;
                                                            if (percentage > 100) {
                                                                setValue(`services.${index}.percentage`, 100);
                                                                newPrice = 0;
                                                            } else {
                                                                setValue(`services.${index}.percentage`, value);
                                                                newPrice =
                                                                    field.oldPrice -
                                                                    field.oldPrice * (percentage / 100);
                                                            }

                                                            setValue(`services.${index}.newPrice`, newPrice.toFixed(2));
                                                        }
                                                    }}
                                                    placeholder="0.0%"
                                                    sx={{
                                                        border: '1.5px solid #D9D9D9',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            border: 'none',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#D9D9D9',
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#D9D9D9',
                                                        },
                                                        '& input': {
                                                            color: 'black',
                                                            fontSize: '0.8rem',
                                                            padding: '5px',
                                                        },
                                                        '& input::placeholder': {
                                                            color: '#747474',
                                                            fontSize: '0.8rem',
                                                            opacity: 1,
                                                        },
                                                        width: { xs: '100%' },
                                                        height: 30,
                                                        backgroundColor: 'white',
                                                        borderRadius: '12px',
                                                    }}
                                                />

                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 400,
                                                        color: '#d50f0f',
                                                    }}
                                                >
                                                    %
                                                </Typography>
                                            </Box>
                                        </React.Fragment>

                                        <Box
                                            id={`services.${index}.oldPrice`}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                flexDirection: { xs: ' column', md: 'row' },
                                                alignItems: { xs: 'start', md: 'center' },
                                                width: { xs: '100%', md: '15%' },
                                                mt: { xs: 2, md: 0 },
                                                ml: { xs: 0, md: 3 },
                                            }}
                                        >
                                            <Typography
                                                display={{ xs: 'block', md: 'none' }}
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 400,
                                                    color: '#1F1F1F',
                                                    mr: 2,
                                                }}
                                            >
                                                {t('SpOffers.OldPr')}
                                            </Typography>
                                            <SecondaryHeading
                                                text={`${field.oldPrice} kr`}
                                                fontColor={'#A0A0A0'}
                                                sx={{
                                                    fontSize: '1rem',
                                                    width: '100%',
                                                    border: { xs: '1px solid #D9D9D9', md: 'none' },
                                                    py: { xs: 0.5, md: '0' },
                                                    px: { xs: 1.5, md: '0' },
                                                    borderRadius: { xs: 2, md: '0' },
                                                }}
                                            />
                                        </Box>

                                        <React.Fragment id={`services.${index}.newPrice`}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: { xs: 'block', md: 'none' },
                                                    fontWeight: 400,
                                                    color: '#1F1F1F',
                                                    width: '100%',
                                                    ml: 1,
                                                    mt: { xs: 2, md: 0 },
                                                }}
                                            >
                                                {t('SpOffers.NewPr')}
                                            </Typography>
                                            <Stack
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                                width={{ xs: '100%', md: '15%' }}
                                            >
                                                <TextField
                                                    {...register(`services.${index}.newPrice`)}
                                                    name={`services.${index}.newPrice`}
                                                    size="medium"
                                                    disabled={!canEditOffers}
                                                    onKeyPress={(e) => {
                                                        const regex = /^([0-9]+(\.[0-9])?)/;
                                                        if (
                                                            !regex.test(e.key) &&
                                                            e.key !== 'Backspace' &&
                                                            e.key !== 'Tab'
                                                        ) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value === '') {
                                                            setValue(`services.${index}.newPrice`, '');
                                                        } else {
                                                            let newPrice = parseFloat(value);

                                                            let percentage = 0;
                                                            if (newPrice > field?.oldPrice) {
                                                                percentage = 100;
                                                                setValue(`services.${index}.newPrice`, field?.oldPrice);
                                                                percentage = 0;
                                                            } else {
                                                                setValue(`services.${index}.newPrice`, newPrice);
                                                                percentage =
                                                                    ((field.oldPrice - newPrice) / field.oldPrice) *
                                                                    100;
                                                            }

                                                            setValue(
                                                                `services.${index}.percentage`,
                                                                percentage.toFixed(2),
                                                            );
                                                        }
                                                    }}
                                                    placeholder="0.00"
                                                    sx={{
                                                        border: '1.5px solid #D9D9D9',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            border: 'none',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#D9D9D9',
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#D9D9D9',
                                                        },
                                                        '& input': {
                                                            color: 'black',
                                                            fontSize: '1rem',
                                                            padding: '5px',
                                                        },
                                                        '& input::placeholder': {
                                                            color: '#747474',
                                                            fontSize: '1rem',
                                                            opacity: 1,
                                                        },
                                                        width: '100%',
                                                        backgroundColor: 'white',
                                                        borderRadius: '12px',
                                                    }}
                                                />
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 400,
                                                        color: '#367B3D',
                                                    }}
                                                >
                                                    Kr.
                                                </Typography>
                                            </Stack>
                                        </React.Fragment>

                                        <FormControl
                                            id={`services.${index}.employeeId`}
                                            sx={{
                                                width: { xs: '100%', md: '20%' },
                                                mt: { xs: 2, md: 0 },
                                                ml: { xs: 0, md: 2 },
                                                overflow: 'visible',
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 400,
                                                    color: '#1F1F1F',
                                                    width: '100%',
                                                    display: { xs: 'block', md: 'none' },
                                                }}
                                            >
                                                {t('SpOffers.WhOffPr')}?
                                            </Typography>
                                            <FSelect
                                                disabled={!canEditOffers}
                                                name={`services.${index}.employeeId`}
                                                value={watchedServices?.[index]?.employeeId || []}
                                                TextToDisplayWithCount={t('SpOffers.EmpSel')}
                                                onChange={(e) => {
                                                    handleCheckboxChange(e, index);
                                                }}
                                                isMultiSelect
                                                options={(profileEmployees || []).map((emp) => ({
                                                    label: emp.name,
                                                    value: emp.id,
                                                }))}
                                            />
                                            {errors?.services?.[index]?.employeeId?.message ? (
                                                <Typography
                                                    variant="caption"
                                                    color="red"
                                                    sx={{ display: 'block', mt: 0.5, lineHeight: 1.2 }}
                                                >
                                                    {errors.services[index].employeeId.message}
                                                </Typography>
                                            ) : null}
                                        </FormControl>

                                        {canEditOffers && (
                                            <IconButton
                                                disableRipple
                                                onClick={() => {
                                                    clearErrors(`services.${index}.employeeId`);
                                                    remove(index);
                                                }}
                                            >
                                                <img
                                                    src={DeleteIcon}
                                                    alt="Delete"
                                                    style={{
                                                        width: '17px',
                                                        height: '19px',
                                                        marginLeft: '@media (max-width: 1440px)' ? 10 : 0,
                                                    }}
                                                />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}

                                <Autocomplete
                                    popupIcon={<KeyboardArrowDownIcon style={{ color: '#d9d9d9' }} />}
                                    value={null}
                                    disabled={!canEditOffers}
                                    options={filteredOptions}
                                    getOptionLabel={(option) => option.name}
                                    inputValue={searchText}
                                    onInputChange={handleSearchInputChange}
                                    groupBy={(option) => option.group}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={`+ ${t('Services.AddService')}`}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                mt: 2,
                                                height: 40,
                                                fontSize: '0.85rem',
                                                borderRadius: '13px',
                                                border: `1px solid #D9D9D9`,
                                                '& .MuiInputBase-root': {
                                                    padding: '5px',
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: '#545454',
                                                    fontSize: '15px',
                                                    fontWeight: 400,
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    border: 'none',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#D9D9D9',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#D9D9D9',
                                                },
                                                '& .MuiAutocomplete-popupIndicator': {
                                                    color: '#000',
                                                },
                                                width: '100%',
                                                '& .MuiInputBase-input::placeholder': {
                                                    color: '#A0A0A0',
                                                    fontSize: '14px',
                                                    fontWeight: 400,
                                                },
                                            }}
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                    renderOption={(props, option) => (
                                        <>
                                            <li
                                                {...props}
                                                key={option.id}
                                                value={option.id}
                                                onClick={(e) => {
                                                    if (option.name !== t('SpOffers.NoServ')) {
                                                        handleServiceSelect(option);
                                                    }
                                                    e.preventDefault();
                                                }}
                                            >
                                                <Typography variant="body1" color="#545454">
                                                    {option.name}
                                                </Typography>
                                            </li>
                                            <Divider sx={{ margin: '8px 0' }} />
                                        </>
                                    )}
                                    renderNoOptions={(props) => (
                                        <Box {...props} sx={{ padding: 1 }}>
                                            <Typography variant="body1" color="error" align="center">
                                                {t('SpOffers.NoRes')}
                                            </Typography>
                                        </Box>
                                    )}
                                    renderGroup={(params) => (
                                        <li>
                                            <ListSubheader
                                                component="div"
                                                sx={{
                                                    fontWeight: '700',
                                                    fontSize: '18px',
                                                    color: '#1F1F1F',
                                                    '&.Mui-disabled': {
                                                        color: '#1F1F1F',
                                                    },
                                                    position: 'relative',
                                                }}
                                            >
                                                {params.group}
                                            </ListSubheader>
                                            <Divider sx={{ margin: '8px 0' }} />
                                            {params.children}
                                        </li>
                                    )}
                                />
                            </Box>

                            <Grid2>
                                <DeepLinkSwitch
                                    formik={formik}
                                    idtype={'cmp'}
                                    selectid={selectedCampaignOffer === null ? 0 : selectedCampaignOffer?.groupId}
                                />
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </form>

                {canEditOffers && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%',
                            marginTop: 4,
                        }}
                    >
                        <CommonButton
                            width={{ xs: '100%', sm: '40%' }}
                            height="40px"
                            backgroundColor={formik.isSubmitting ? '#BDBDBD' : '#44B904'}
                            onClick={handleCampaignSubmit}
                            disabled={formik.isSubmitting}
                            loading={formik.isSubmitting}
                            title={type == 'edit' ? t('SpOffers.UpCmp') : t('SpOffers.AddCmp')}
                        />
                    </Box>
                )}
            </Paper>
        </Modal>
    );
}

export default AddCampaignOfferModal;
